import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const currency = typeof Intl !== 'undefined'
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
  : null;

const formatMoney = (value) => {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '';
  return currency ? currency.format(num) : `$${num.toFixed(2)}`;
};

export default function CommunityBuildDetail() {
  const { buildId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [build, setBuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ description: '', tags: '' });
  const [voteBusy, setVoteBusy] = useState(false);
  const [userVoted, setUserVoted] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = user?.id && build?.user_id && user.id === build.user_id;
  const canDelete = canEdit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/community-builds/${buildId}`);
      setBuild(data?.build || null);
      setDraft({
        description: data?.build?.description || '',
        tags: Array.isArray(data?.build?.tags) ? data.build.tags.join(', ') : '',
      });
      setUserVoted(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to load build.');
      nav('/community-builds');
    } finally {
      setLoading(false);
    }
  }, [buildId, nav]);

  useEffect(() => {
    if (buildId) load();
  }, [buildId, load]);

  const save = async () => {
    const tagsArr = draft.tags.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      const { data } = await api.patch(`/community-builds/${buildId}`, {
        description: draft.description,
        tags: tagsArr,
      });
      setBuild(data?.build || null);
      setEditing(false);
      toast.success('Build updated.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to update build.');
    }
  };

  const toggleVote = async () => {
    if (!user) {
      toast.error('Sign in to vote.');
      return;
    }
    setVoteBusy(true);
    try {
      const action = userVoted ? 'delete' : 'post';
      const { data } = await api[action](`/community-builds/${buildId}/vote`);
      setBuild((prev) => (prev ? { ...prev, vote_count: Number(data.vote_count) || 0 } : prev));
      setUserVoted(data.voted);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to vote.');
    } finally {
      setVoteBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/community-builds/${buildId}`);
      toast.success('Build deleted.');
      nav('/community-builds');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to delete build.');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return <p className="px-4 py-6 text-sm text-zinc-400">Loading build...</p>;
  }

  if (!build) {
    return <p className="px-4 py-6 text-sm text-zinc-400">Build not found.</p>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 text-zinc-100 space-y-4">
      <button
        type="button"
        className="text-sm text-zinc-300 hover:text-white"
        onClick={() => nav('/community-builds')}
      >
        ← Back to Community Builds
      </button>

      <div className="glass-card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-amber-200">{build.title}</h1>
            <p className="text-sm text-zinc-400">
              Posted by {build.username || 'unknown'} · {build.created_at ? new Date(build.created_at).toLocaleString() : ''}
            </p>
            {Array.isArray(build.tags) && build.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {build.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="text-[11px] uppercase tracking-wide rounded-full bg-white/5 px-2 py-1 text-zinc-300 border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          {build.total_cost && (
            <span className="rounded-full bg-amber-500/10 px-3 py-1 text-sm text-amber-200 border border-amber-400/30">
              Total: {formatMoney(build.total_cost)}
            </span>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleVote}
              disabled={voteBusy}
              className={`rounded-full border px-3 py-1 text-xs ${
                userVoted
                  ? 'border-amber-400/50 bg-amber-500/10 text-amber-200'
                  : 'border-white/10 bg-white/5 text-zinc-200 hover:border-amber-300/60 hover:text-amber-200'
              }`}
            >
              {userVoted ? 'Upvoted' : 'Upvote'}
            </button>
            <span className="text-xs text-zinc-400">
              {build.vote_count === 1 ? '1 vote' : `${build.vote_count || 0} votes`}
            </span>
            {canDelete && (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={deleteBusy}
                className="ml-2 rounded-md border border-red-400/40 px-3 py-1 text-xs text-red-200 hover:bg-red-500/10"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {build.render_url && (
          <div className="overflow-hidden rounded-xl border border-white/10 bg-black/30">
            <img src={build.render_url} alt={build.title} className="block w-full object-cover" />
          </div>
        )}

        {editing ? (
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Description</label>
              <textarea
                className="w-full rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                rows={4}
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Tags (comma separated)</label>
              <input
                className="w-full rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                value={draft.tags}
                onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))}
                placeholder="modern, kitchen, minimal"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="rounded-md border border-white/15 px-3 py-2 text-sm text-zinc-200 hover:bg-white/5"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gradient-to-r from-amber-300 to-amber-500 px-3 py-2 text-sm font-semibold text-zinc-900 shadow disabled:opacity-60"
                onClick={save}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          build.description && <p className="text-sm text-zinc-200 leading-relaxed">{build.description}</p>
        )}

        {canEdit && !editing && (
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-md border border-white/15 px-3 py-2 text-xs text-zinc-200 hover:bg-white/5"
              onClick={() => setEditing(true)}
            >
              Edit description / tags
            </button>
          </div>
        )}

        {Array.isArray(build.materials) && build.materials.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-black/30 text-zinc-300">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Qty</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Cost</th>
                </tr>
              </thead>
              <tbody>
                {build.materials.map((m, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="px-3 py-2 text-zinc-100">
                      <div className="font-medium">{m.item || '--'}</div>
                      {m.notes && <div className="text-xs text-zinc-400">{m.notes}</div>}
                      {m.product_link && (
                        <a
                          href={m.product_link}
                          className="text-xs text-amber-300 hover:text-amber-200"
                          target="_blank"
                          rel="noreferrer"
                        >
                          View link
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-200">{m.quantity || ''}</td>
                    <td className="px-3 py-2 text-zinc-200">{m.unit || ''}</td>
                    <td className="px-3 py-2 text-zinc-200">{m.source || ''}</td>
                    <td className="px-3 py-2 text-zinc-200">
                      {m.cost != null ? formatMoney(Number(m.cost)) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !deleteBusy && setDeleteOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900/90 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-amber-200 mb-2">Delete build?</h3>
            <p className="text-sm text-zinc-300">
              {build.title || 'This build'} will be removed. This action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-white/15 px-3 py-2 text-sm text-zinc-200 hover:bg-white/5"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-red-400/40 bg-red-500/20 px-3 py-2 text-sm font-semibold text-red-100 hover:bg-red-500/30 disabled:opacity-60"
                onClick={handleDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
